import { Component, OnInit, Renderer2, Output, EventEmitter, Type as AngularCoreType, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DecimalFormatDirective } from '../../../directives/decimal-format-directive';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { DecimalInputFormFieldBaseComponent } from '../num-input-form-field-base/decimal-input-form-field-base.component';
import { DecimalPipe } from '@angular/common';
import { state, style, transition, trigger, animate } from '@angular/animations';

@Component({
    selector: 'app-numeric-form-field',
    templateUrl: './numeric-form-field.component.html',
    styleUrls: [
        './numeric-form-field.component.scss',
        '../form-fields.scss',
        '../num-input-form-field-base/num-input-form-field-base.component.scss',
        '../currency-form-field/currency-form-field.component.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NumericFormFieldComponent,
            multi: true
        }
    ],
    standalone: false,
    animations: [
        trigger('fadeAnimation', [
            state('visible', style({ opacity: 1 })),
            state('hidden', style({ opacity: 0 })),
            transition('visible => hidden', [
                animate('1s ease-out') // Fade out over 1 second
            ])
        ])
    ]
})
export class NumericFormFieldComponent extends DecimalInputFormFieldBaseComponent implements OnInit {
    // Properties.
    private userChangedFieldValue: boolean = false;
    private formattedPreviewValue: string = '';
    private formattingError: string;
    public formattingErrorState: string = 'hidden';
    public hasFocus: boolean = false;

    @ViewChild(DecimalFormatDirective, { read: DecimalFormatDirective }) decimalFormatDirective: DecimalFormatDirective;

    public constructor(rendererParam: Renderer2) {
        super(rendererParam);
        this.matInputId = this.generateUniqueId('numeric');
    }

    // Life cycle methods.
    public ngOnInit(): void {
        let hshProperties = this.getProperties();
        this.onInit.emit(hshProperties);
    }

    // Form field methods.
    public getProperties(): any {
        let properties: any = super.getProperties();

        let propertyNames: FormFieldPropertyEnum[] = <FormFieldPropertyEnum[]>properties['properties'];
        propertyNames.push(FormFieldPropertyEnum.NUM_DIGITS_AFTER_DECIMAL_POINT);
        propertyNames.push(FormFieldPropertyEnum.SHOW_TRAILING_ZEROES_AFTER_DECIMAL_POINT);
        propertyNames.push(FormFieldPropertyEnum.SHOW_DOLLAR_SIGN_PREFIX);

        return properties;
    }

    // Override a method used to get my class.
    public getFormFieldClass(): AngularCoreType<any> {
        return (NumericFormFieldComponent);
    }

    public onChange() {
        this.formattedPreviewValue = this.decimalFormatDirective.applyDecimalFormatting(this.FormControl.value);
    }

    // Methods called by my HTML code.
    public get DecimalStepIncrementExpr(): number {
        let increment: number = 1;
        for (let index: number = 0; index < this.FormField.numDigitsAfterDecimalPoint; index++)
            increment /= 10;
        return increment;
    }

    public get DigitsToShow(): number {
        return (this.FormField.numDigitsAfterDecimalPoint);
    }

    public get ShowTrailing(): boolean {
        return (this.FormField.showTrailingZeroesAfterDecimalPoint);
    }

    public get NumberFormatPreview(): string {
        return this.formattedPreviewValue;
    }

    public get ShowCurrencyPrefix(): boolean {
        return this.FormField.showDollarSignPrefix;
    }

    public get FormattingError(): string {
        // As well as returning the error message, set up for the fading out of the message
        if (this.formattingError != this.decimalFormatDirective?.ErrorMessage) {
            this.formattingError = this.decimalFormatDirective?.ErrorMessage;
            this.formattingErrorState = 'visible';
            setTimeout(() => { this.formattingErrorState = 'hidden' }, 1000);
        }
        return this.formattingError;
    }

    // Define a method called before a form instance save.
    public CheckFieldForValidation(): void {
        super.CheckFieldForValidation();
    }

    public saveData(formInstance: any): void {
        if (!this.userChangedFieldValue) {
            if ((this.FormInstanceElement.transientOriginalDecimalValue != null) && (this.FormInstanceElement.transientOriginalDecimalValue != this.FormInstanceElement.decimalValue))
                this.FormInstanceElement.decimalValue = this.FormInstanceElement.transientOriginalDecimalValue;
        }
        this.userChangedFieldValue = false;
    }

    // Override a protected method. (formerly named writeValueTriggered)
    protected initalizeComponentFormControl(): void {
        super.initalizeComponentFormControl();

        if (this.controlData.formControl && this.FormInstanceElement.decimalValue != null && this.decimalFormatDirective) {
            this.decimalFormatDirective.writeValue(this.FormInstanceElement.decimalValue);
            this.controlData.formControl.setValue(this.decimalFormatDirective.value);
        }

        let setVal = this.FormControl?.value;

        if (this.formField && this.controlData.formControl && this.decimalFormatDirective) {
            setVal = this.decimalFormatDirective.applyDecimalFormatting(this.controlData.formControl.value);
            this.controlData.formControl.setValue(setVal);
        }

        // Make sure the user doesn't get a "you have unsaved changes" message
        this.FormInstanceElement.UserUpdatedData = false;
    }

    public handleOnModelChange(): void {
        if (this.decimalFormatDirective) {
            this.decimalFormatDirective.writeValue(this.FormInstanceElement['unformattedDecimalValue']);
            this.FormInstanceElement['unformattedDecimalValue'] = null;
        }
        super.handleOnModelChange();

        return;
    }

    public handleOnFocus(eventData: FocusEvent): void {
        this.hasFocus = true;
        this.formattedPreviewValue = this.decimalFormatDirective.applyDecimalFormatting(this.FormControl.value);
    }

    public handleOnBlur() {
        this.hasFocus = false;
        this.notifyControlTouched();
    }

}
